import { createAction } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { setActiveDrawableGroup } from '../../../actions/drawable'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectActiveGeometryGroupId } from '../../slices/geometry'
import { selectAreMaterialInteractionsBlocked, selectIsConflictResolutionInProgress } from '../../slices/masterSetPlan'

export const selectConflictMaterialAction = createAction<number>('selectConflictMaterial')

// Conflict selection is independent of material groups and their settings.
export function* handleSelectConflictMaterial({ payload }: ReturnType<typeof selectConflictMaterialAction>) {
    const isResolving: boolean = yield select(selectIsConflictResolutionInProgress)
    const isBlocked: boolean = yield select(selectAreMaterialInteractionsBlocked)

    if (!isResolving || isBlocked) return

    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [selectTool, workspaceTool]: [Select, Workspace] = yield call(manager.getTools, [Select.NAME, Workspace.NAME])
    const items: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) =>
            data.materialModificationConflict?.material_location_id === payload ||
            (data.createdDuringConflictResolution && data.opening_location_id === payload)
    )
    const item = items.find((candidate) => {
        if (!candidate.visible) return false

        // Labeled materials carry the same metadata on their group and path.
        for (let parent = candidate.parent; parent; parent = parent.parent) {
            if (!parent.visible || items.includes(parent)) return false
        }

        return true
    })
    const activeGroupId: number | null = yield select(selectActiveGeometryGroupId)

    if (activeGroupId !== null) yield put(setActiveDrawableGroup(null))

    yield call(manager.useTool, Select.NAME)
    yield call(selectTool.selectConflictItem, item ?? null)
}

export function* watchForSelectConflictMaterial() {
    yield takeEvery(selectConflictMaterialAction.type, handleSelectConflictMaterial)
}
